import { useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { newId } from '../../doc/BoardDoc';
import type { KanbanCard, KanbanColumn, KanbanItem } from '../../doc/schema';
import { IconClose } from '../../ui/icons';
import { useBoard, usePane } from '../context';

interface Drop {
  colId: string;
  index: number;
}

/**
 * Kanban board item. Static until double-clicked; in edit mode columns and cards can be added,
 * renamed, deleted and dragged between columns with any pointer (mouse, touch, pen).
 * Columns are stored as one JSON value, so two people editing the same board at the same
 * second get last-writer-wins on the whole kanban (fine for a personal tool).
 */
export function KanbanBody({ item }: { item: KanbanItem }) {
  const board = useBoard();
  const editing = usePane((s) => s.editingId === item.id);
  const cols = item.props.columns;
  const [editCard, setEditCard] = useState<string | null>(null);
  const [editCol, setEditCol] = useState<string | null>(null);
  const [drop, setDrop] = useState<Drop | null>(null);
  const drag = useRef<{ pointerId: number; cardId: string; fromCol: string; moved: boolean } | null>(null);

  const commit = (columns: KanbanColumn[]) => board.updateItem(item.id, { props: { columns } });
  const patchCol = (id: string, fn: (c: KanbanColumn) => KanbanColumn) => commit(cols.map((c) => (c.id === id ? fn(c) : c)));

  const addColumn = () => commit([...cols, { id: newId(), title: 'New column', cards: [] }]);
  const deleteColumn = (id: string) => {
    const c = cols.find((x) => x.id === id);
    if (c?.cards.length && !confirm(`Delete "${c.title}" and its ${c.cards.length} cards?`)) return;
    commit(cols.filter((x) => x.id !== id));
  };
  const addCard = (colId: string) => {
    const card: KanbanCard = { id: newId(), text: '' };
    patchCol(colId, (c) => ({ ...c, cards: [...c.cards, card] }));
    setEditCard(card.id);
  };
  const setCardText = (colId: string, cardId: string, text: string) =>
    patchCol(colId, (c) => ({ ...c, cards: c.cards.map((k) => (k.id === cardId ? { ...k, text } : k)) }));
  const deleteCard = (colId: string, cardId: string) =>
    patchCol(colId, (c) => ({ ...c, cards: c.cards.filter((k) => k.id !== cardId) }));

  const moveCard = (cardId: string, fromCol: string, to: Drop) => {
    const from = cols.find((c) => c.id === fromCol);
    const card = from?.cards.find((k) => k.id === cardId);
    if (!from || !card) return;
    let index = to.index;
    if (fromCol === to.colId) {
      const cur = from.cards.indexOf(card);
      if (cur < index) index--;
      if (cur === index) return;
    }
    commit(
      cols.map((c) => {
        const cards = c.cards.filter((k) => k.id !== cardId);
        if (c.id === to.colId) cards.splice(Math.min(index, cards.length), 0, card);
        return { ...c, cards };
      }),
    );
  };

  const dropAt = (clientX: number, clientY: number): Drop | null => {
    const el = document.elementFromPoint(clientX, clientY);
    const colEl = el?.closest<HTMLElement>('[data-col-id]');
    if (!colEl) return null;
    const colId = colEl.dataset.colId!;
    const cards = [...colEl.querySelectorAll<HTMLElement>('[data-card-id]')];
    let index = cards.length;
    for (let i = 0; i < cards.length; i++) {
      const r = cards[i]!.getBoundingClientRect();
      if (clientY < r.top + r.height / 2) {
        index = i;
        break;
      }
    }
    return { colId, index };
  };

  const onCardPointerDown = (e: ReactPointerEvent, colId: string, cardId: string) => {
    if (!editing || editCard === cardId) return;
    if ((e.target as HTMLElement).closest('button')) return;
    e.stopPropagation();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    drag.current = { pointerId: e.pointerId, cardId, fromCol: colId, moved: false };
  };
  const onCardPointerMove = (e: ReactPointerEvent) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    d.moved = true;
    setDrop(dropAt(e.clientX, e.clientY));
  };
  const onCardPointerUp = (e: ReactPointerEvent) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    drag.current = null;
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    setDrop(null);
    if (!d.moved) {
      setEditCard(d.cardId);
      return;
    }
    const target = dropAt(e.clientX, e.clientY);
    if (target) moveCard(d.cardId, d.fromCol, target);
  };

  return (
    <div
      className={`kanban${editing ? ' is-editing' : ''}`}
      onPointerDown={editing ? (e) => e.stopPropagation() : undefined}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {cols.map((col) => (
        <div key={col.id} className={`kb-col${drop?.colId === col.id ? ' is-drop' : ''}`} data-col-id={col.id}>
          <div className="kb-head">
            {editing && editCol === col.id ? (
              <input
                className="kb-title-input"
                defaultValue={col.title}
                autoFocus
                onBlur={(e) => {
                  patchCol(col.id, (c) => ({ ...c, title: e.target.value.trim() || c.title }));
                  setEditCol(null);
                }}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === 'Enter' || e.key === 'Escape') (e.target as HTMLInputElement).blur();
                }}
              />
            ) : (
              <button type="button" className="kb-title" onClick={() => editing && setEditCol(col.id)} title={editing ? 'Rename column' : undefined}>
                {col.title}
              </button>
            )}
            <span className="kb-count">{col.cards.length}</span>
            {editing && (
              <button type="button" className="kb-x" title="Delete column" onClick={() => deleteColumn(col.id)}>
                <IconClose />
              </button>
            )}
          </div>
          <div className="kb-cards">
            {col.cards.map((card, i) => (
              <div key={card.id} className="kb-slot">
                {drop?.colId === col.id && drop.index === i && <div className="kb-drop-line" />}
                <div
                  className={`kb-card${editCard === card.id ? ' is-editing' : ''}`}
                  data-card-id={card.id}
                  onPointerDown={(e) => onCardPointerDown(e, col.id, card.id)}
                  onPointerMove={onCardPointerMove}
                  onPointerUp={onCardPointerUp}
                  onPointerCancel={onCardPointerUp}
                >
                  {editing && editCard === card.id ? (
                    <textarea
                      className="kb-card-input"
                      defaultValue={card.text}
                      autoFocus
                      rows={2}
                      onPointerDown={(e) => e.stopPropagation()}
                      onBlur={(e) => {
                        setCardText(col.id, card.id, e.target.value.trim());
                        setEditCard(null);
                      }}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === 'Escape' || (e.key === 'Enter' && !e.shiftKey)) {
                          e.preventDefault();
                          (e.target as HTMLTextAreaElement).blur();
                        }
                      }}
                    />
                  ) : (
                    <div className="kb-card-text">{card.text || <span className="placeholder">Empty card</span>}</div>
                  )}
                  {editing && editCard !== card.id && (
                    <button type="button" className="kb-x kb-card-x" title="Delete card" onClick={() => deleteCard(col.id, card.id)}>
                      <IconClose />
                    </button>
                  )}
                </div>
              </div>
            ))}
            {drop?.colId === col.id && drop.index >= col.cards.length && <div className="kb-drop-line" />}
          </div>
          {editing && (
            <button type="button" className="kb-add" onClick={() => addCard(col.id)}>
              + Add card
            </button>
          )}
        </div>
      ))}
      {editing && (
        <button type="button" className="kb-add kb-add-col" onClick={addColumn}>
          + Column
        </button>
      )}
      {!editing && cols.every((c) => c.cards.length === 0) && (
        <div className="placeholder kanban-placeholder">Double-click to edit the board</div>
      )}
    </div>
  );
}
